import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AbstractWSDepartureService } from '../abstract-ws-departure.service';
import {
  Centrifuge,
  PublicationContext,
  RpcResult,
  ServerPublicationContext,
  State,
  Subscription,
} from 'centrifuge';
import {
  AlarmTag,
  Departure,
  DepartureDetails,
  DepartureStatus,
  DepartureWSMessage,
  DepartureWSMessageType,
  DepartureWSMethod,
  RawDepartureResponse,
  Report,
  ReportStatus,
  ReportWSMessageType,
  Team,
  TeamStatus,
} from '../../../models/departure.model';
import { BehaviorSubject, filter, firstValueFrom, Observable, Subject, take, timeout } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { SoundService } from '../../sound-service/sound.service';
import {
  RequestStatus,
  TeamRequest,
  TeamRequestInfo,
  TeamShiftInfo,
} from '../../../views/team-shift/models/team-shift.model';
import { NotificationsService } from '../../notifications-service/notifications.service';
import {
  sortAscendingCompanyName,
  sortAscendingTeamName,
} from '../../../../common/utils/ascending-sorting';
import { NavbarHeightService } from '../../navbar-height-service/navbar-height.service';
import { Mission, MissionStatisticsCity } from '../../../models/mission.model';
import { mapMissionPublication, MissionChangePublication } from './mission-publication';
import { ErrorNotificationService } from '../../error-notification-service/error-notification.service';

const CONNECTION_ERROR_MESSAGE =
  'Нет соединения с сервером. Проверьте интернет и повторите действие.';
const RPC_ERROR_MESSAGE = 'Не удалось выполнить запрос. Попробуйте ещё раз.';
const SESSION_EXPIRED_MESSAGE = 'Время сеанса истекло. Войдите в аккаунт снова.';
const RPC_TIMEOUT = 30000;
const DRAWER_RPC_TIMEOUT = 10000;

type DepartureHistorySearchParams = {
  limit: number;
  page: number;
  greater_than?: number;
  less_or_equal?: number;
  departure_number?: string;
  object_number?: string;
  team_names?: string[];
  alarm_tags?: AlarmTag[];
};

@Injectable({
  providedIn: 'root',
})
export class WSDepartureService implements AbstractWSDepartureService {
  private readonly router = inject(Router);
  private readonly navbarService = inject(NavbarHeightService);
  private readonly soundService = inject(SoundService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly errorNotificationService = inject(ErrorNotificationService);
  private centrifuge!: Centrifuge;
  private isConnectionErrorShown = false;
  private readonly allDeparturesController = new BehaviorSubject<Array<Departure> | null>(null);
  readonly allDepartures$: Observable<Departure[] | null> =
    this.allDeparturesController.asObservable();
  private readonly detailsMessagesController = new Subject<DepartureDetails>();
  readonly detailsMessages$: Observable<DepartureDetails> =
    this.detailsMessagesController.asObservable();
  private readonly reportMessagesController = new Subject<Report>();
  readonly reportMessages$: Observable<Report> = this.reportMessagesController.asObservable();
  private readonly cancelReportSavedController = new Subject<string>();
  readonly cancelReportSaved$: Observable<string> = this.cancelReportSavedController.asObservable();
  private readonly connectedController = new Subject<void>();
  readonly connected$: Observable<void> = this.connectedController.asObservable();
  readonly canConnectToCentrifugeController = new BehaviorSubject<boolean>(false);
  private readonly historyMessagesController = new Subject<RawDepartureResponse>();
  readonly historyMessages$: Observable<RawDepartureResponse> =
    this.historyMessagesController.asObservable();
  readonly teamsStateController = new BehaviorSubject<Team[]>([]);
  readonly teams$: Observable<Team[]> = this.teamsStateController.asObservable();
  private teamArrivedChannel!: Subscription;
  private teamDepartedChannel!: Subscription;
  private teamReleasedChannel!: Subscription;
  private readonly requestsController = new BehaviorSubject<TeamRequest[]>([]);
  readonly requests$ = this.requestsController.asObservable();
  totalRequestsNumber = signal(0);
  private temporaryDeparturesStorage: Departure[] &
    { id: string; statuses: { status: DepartureStatus; created_at: number }[] }[] = [];
  currentRequestInfo: WritableSignal<null | TeamRequestInfo | TeamShiftInfo> = signal(null);
  pendingRequestsCount = signal(0);
  pendingRequestsController = new BehaviorSubject<TeamRequest[]>([]);
  pendingRequests$ = this.pendingRequestsController.asObservable();
  private readonly teamsSummaryController = new BehaviorSubject<
    {
      company_name: string;
      teamsWithShiftChange?: number;
      underNominatedTeams?: number;
      teamsWithoutShiftChange?: number;
      summaries: {
        state: 'NoShifts' | 'NoRotations' | 'ClosedSince' | '';
        closed_since: number | null;
        team_id: string;
        team_name: string;
        summaries:
          | {
              actual_members: number;
              created_at: number;
              closed_at?: number;
              driver: string;
              planned_members: number;
              shift_id: string;
              team_id: string;
              shift_initiator: 'ShiftRotation' | 'ShiftOpening' | 'ShiftClosing';
              team_request_time: number;
              request_resolved_time: number;
              intern?: string;
              guard?: string;
            }[]
          | null;
      }[];
    }[]
  >([]);
  readonly teamsSummary$ = this.teamsSummaryController.asObservable();
  private readonly allPartnersControl = new BehaviorSubject<string[]>([]);
  readonly allPartners$ = this.allPartnersControl.asObservable();
  readonly allStatisticsCompaniesControl = new BehaviorSubject<
    { name: string; id: string; isPartner: boolean }[]
  >([]);
  readonly allStatisticsCompanies$ = this.allStatisticsCompaniesControl.asObservable();
  private readonly citiesController = new BehaviorSubject<MissionStatisticsCity[] | null>(null);
  readonly cities$ = this.citiesController.asObservable();
  private readonly citiesLoadingErrorController = new BehaviorSubject<boolean>(false);
  readonly citiesLoadingError$ = this.citiesLoadingErrorController.asObservable();
  private shouldLoadCitiesAfterConnection = false;
  private pendingHistorySearchParams: DepartureHistorySearchParams | null = null;
  private readonly newMission = new Subject<Mission | undefined>();
  readonly newMission$ = this.newMission.asObservable();
  private readonly changedMission = new Subject<MissionChangePublication>();
  readonly changedMission$ = this.changedMission.asObservable();
  private readonly teamDepartureUpdates = new Map<
    string,
    { departureId: string; number: number; stage: number; timestamp: number }
  >();
  private readonly pendingCancelReportIds = new Set<string>();
  private readonly savedCancelReportIds = new Set<string>();

  initCentrifuge(): void {
    this.isConnectionErrorShown = false;
    const token = localStorage.getItem('rsToken') || '';
    this.centrifuge = new Centrifuge(environment.ws, {
      token,
      timeout: 30000,
    });

    if (!this.centrifuge) {
      return;
    }

    this.centrifuge.on('connecting', (context): void => {
      if (context.code !== 0) {
        this.showConnectionError();
      }
    });
    this.centrifuge.on('error', (): void => {
      if (this.centrifuge.state !== State.Connected) {
        this.showConnectionError();
      }
    });
    this.centrifuge.on('connected', (): void => {
      this.isConnectionErrorShown = false;
      this.connectedController.next();
      if (this.errorNotificationService.errorMessage() === CONNECTION_ERROR_MESSAGE) {
        this.errorNotificationService.closeError();
      }
      this.loadInitialData();
      if (this.shouldLoadCitiesAfterConnection) {
        this.shouldLoadCitiesAfterConnection = false;
        void this.getCities();
      }
      this.loadPendingDeparturesHistory();
    });
    this.centrifuge.on('publication', (publication: ServerPublicationContext): void => {
      const isMissionPublication = publication.channel.endsWith(':missions');

      if (
        publication.channel.endsWith(':reports') &&
        publication.data.detail_type === ReportWSMessageType.CancelSaved &&
        typeof publication.data.detail?.departure_id === 'string'
      ) {
        this.markCancelReportSaved(publication.data.detail.departure_id);
        this.cancelReportSavedController.next(publication.data.detail.departure_id);
        return;
      }

      if (
        (publication.data.detail_type === ReportWSMessageType.Approved ||
          publication.data.detail_type === ReportWSMessageType.Declined ||
          publication.data.detail_type === ReportWSMessageType.Filled) &&
        this.allDeparturesController.value
      ) {
        let status!: ReportStatus;
        if (publication.data.detail_type === ReportWSMessageType.Approved) {
          status = ReportStatus.Approved;
        } else if (publication.data.detail_type === ReportWSMessageType.Declined) {
          status = ReportStatus.Declined;
        } else {
          status = ReportStatus.Filled;
        }

        const departures: Departure[] = this.allDeparturesController.value.map(
          (departure: Departure): Departure => {
            if (departure.id === publication.data.detail.departure_id) {
              return {
                ...departure,
                report_status: status,
              };
            }
            return departure;
          },
        );
        this.allDeparturesController.next(departures);
      } else if ('request_id' in publication.data && 'status' in publication.data) {
        const index = this.requestsController.value.findIndex((request) => {
          return request.request_id === publication.data.request_id;
        });

        const reqs = this.requestsController.value;
        if (index !== -1 && reqs[index].request_status === RequestStatus.Pending) {
          this.pendingRequestsController.next(
            this.pendingRequestsController.value.filter((r) => {
              return r.request_id !== publication.data.request_id;
            }),
          );
          this.pendingRequestsCount.update((value) => (value -= 1));

          reqs[index] = { ...reqs[index], request_status: publication.data.status };
          if (publication.data.status === RequestStatus.Approved) {
            const currentTeam: Team | undefined = this.teamsStateController.value.find(
              (team: Team): boolean => {
                return team.id === reqs[index].team_id;
              },
            );
            this.notificationsService.showNotification(
              'approved',
              currentTeam ? currentTeam.name : 'Группа не определена',
            );
          }
          if (publication.data.status === RequestStatus.Declined) {
            const currentTeam: Team | undefined = this.teamsStateController.value.find(
              (team: Team): boolean => {
                return team.id === reqs[index].team_id;
              },
            );
            this.notificationsService.showNotification(
              'declined',
              currentTeam ? currentTeam.name : 'Группа не определена',
            );
          }
          this.requestsController.next(reqs);

          if (
            this.currentRequestInfo() &&
            this.currentRequestInfo()!.request_id === publication.data.request_id
          ) {
            this.currentRequestInfo.update((value) => {
              return { ...value, request_status: publication.data.status as RequestStatus } as
                | TeamShiftInfo
                | TeamRequestInfo;
            });
          }
        } else {
          const pendReq = this.pendingRequestsController.value.findIndex((re) => {
            return re.request_id === publication.data.request_id;
          });

          if (
            pendReq !== -1 &&
            this.pendingRequestsController.value[pendReq].request_status === RequestStatus.Pending
          ) {
            this.pendingRequestsController.next(
              this.pendingRequestsController.value.filter((r) => {
                return r.request_id !== publication.data.request_id;
              }),
            );
            this.pendingRequestsCount.update((value) => (value -= 1));
          }
        }
      } else if ('request_id' in publication.data && 'request_status' in publication.data) {
        this.totalRequestsNumber.update((value) => value++);
        this.requestsController.next([publication.data, ...this.requestsController.value]);
        if (publication.data.request_status === RequestStatus.Pending) {
          this.notificationsService.showNotification('new');
          this.pendingRequestsController.next([
            ...this.pendingRequestsController.value,
            publication.data,
          ]);
          this.pendingRequestsCount.update((value) => (value += 1));
        }
      } else if (
        publication.data.event === 'ShiftClosed' ||
        publication.data.event === 'ShiftOpened'
      ) {
        const teamIndex = this.teamsStateController.value.findIndex((team: Team): boolean => {
          return team.id === publication.data.team_id;
        });
        const teams: Team[] = this.teamsStateController.value;

        teams[teamIndex] = {
          ...teams[teamIndex],
          status: TeamStatus.TeamAvailable,
          object_number: null,
          is_on_shift: publication.data.event !== 'ShiftClosed',
        };

        this.teamsStateController.next(teams);
      } else if (isMissionPublication) {
        const missionPublication = mapMissionPublication(publication.data);

        if (missionPublication?.type === 'new') {
          this.newMission.next(missionPublication.mission);
        } else if (missionPublication?.type === 'changed') {
          this.changedMission.next(missionPublication.change);
        }
      } else {
        this.handleDepartureMessage(publication.data);
      }
    });

    this.centrifuge.on('disconnected', (): void => {
      localStorage.removeItem('rsToken');

      this.unsubscribeCentrifuge();
      this.canConnectToCentrifugeController.next(false);
      this.router.navigate(['/auth']);
    });

    this.centrifuge.connect();

    this.teamArrivedChannel = this.centrifuge.newSubscription('team:arrived');
    this.teamArrivedChannel.on('publication', (publication: PublicationContext): void => {
      this.handleTeamMessage(publication.data.detail, TeamStatus.TeamOnObject);
    });
    this.teamArrivedChannel.subscribe();

    this.teamDepartedChannel = this.centrifuge.newSubscription('team:departed');
    this.teamDepartedChannel.on('publication', (publication: PublicationContext): void => {
      this.handleTeamMessage(publication.data.detail, TeamStatus.TeamEnRoute);
    });
    this.teamDepartedChannel.subscribe();

    this.teamReleasedChannel = this.centrifuge.newSubscription('team:released');
    this.teamReleasedChannel.on('publication', (publication: PublicationContext): void => {
      this.handleTeamMessage(publication.data.detail, TeamStatus.TeamAvailable);
    });
    this.teamReleasedChannel.subscribe();
  }

  private loadInitialData(): void {
    void this.getTeams();
    void this.executeRpc('departures:GetForDispatcher', {}).then(
      async (response): Promise<void> => {
        if (response) {
          await this.restoreDepartureBoard(response);
          return;
        }

        let retryNumber = 0;
        const interval = setInterval((): void => {
          retryNumber += 1;
          void this.executeRpc('departures:GetForDispatcher', {}).then(
            async (retryResponse): Promise<void> => {
              if (retryResponse) {
                clearInterval(interval);
                await this.restoreDepartureBoard(retryResponse);
              }
            },
          );
          if (retryNumber === 5) {
            clearInterval(interval);
          }
        }, 31000);
      },
    );
  }

  private async restoreDepartureBoard(response: RpcResult): Promise<void> {
    const missingDepartures = await this.loadPendingCancelReportIds(response.data);
    this.setAllDeparturesValue({ ...response, data: [...response.data, ...missingDepartures] });
  }

  private setAllDeparturesValue(response: RpcResult): void {
    if (this.temporaryDeparturesStorage.length) {
      const newDepartures: Departure[] = this.temporaryDeparturesStorage.filter(
        (savedData): boolean => {
          return 'team_name' in savedData;
        },
      ) as Departure[];
      const allDepartures: Departure[] = [...response.data, ...newDepartures] as Departure[];

      const newStates: {
        id: string;
        statuses: { status: DepartureStatus; created_at: number }[];
      }[] = this.temporaryDeparturesStorage.filter((savedData): boolean => {
        return !('team_name' in savedData);
      });
      newStates.forEach((newState): void => {
        const departureNumber: number = allDepartures.findIndex((departure: Departure): boolean => {
          return departure.id === newState.id;
        });
        if (departureNumber === -1) {
          return;
        }
        allDepartures[departureNumber] = {
          ...allDepartures[departureNumber],
          statuses: [...allDepartures[departureNumber].statuses, ...newState.statuses],
        };
      });
      this.allDeparturesController.next(this.markPendingCancelReports(allDepartures));
    } else {
      this.allDeparturesController.next(this.markPendingCancelReports(response.data));
    }
  }

  private markPendingCancelReports(departures: Departure[]): Departure[] {
    return departures.map((departure) => ({
      ...departure,
      awaitingCancelReport: this.pendingCancelReportIds.has(departure.id),
    }));
  }

  private async loadPendingCancelReportIds(currentDepartures: Departure[]): Promise<Departure[]> {
    const pendingIds = new Map<string, number>();
    let beforeNumber: number | null = null;

    do {
      const response = await this.executeRpc('departures:GetPendingCancelReports', {
        limit: 100,
        ...(beforeNumber === null ? {} : { before_number: beforeNumber }),
      });
      if (!response) {
        return [];
      }
      for (const item of response.data.items as { departure_id: string; number: number }[]) {
        if (!this.savedCancelReportIds.has(item.departure_id)) {
          pendingIds.set(item.departure_id, item.number);
        }
      }
      beforeNumber = response.data.next_before_number ?? null;
    } while (beforeNumber !== null);

    this.pendingCancelReportIds.clear();
    pendingIds.forEach((_, id) => this.pendingCancelReportIds.add(id));

    // The dispatch board only contains eight hours / 100 recent departures.
    // Restore older pending cards through the existing scoped history API.
    const currentIds = new Set(currentDepartures.map((departure) => departure.id));
    const missing = [...pendingIds].filter(([id]) => !currentIds.has(id));
    const restored: Departure[] = [];
    for (let offset = 0; offset < missing.length; offset += 5) {
      const results = await Promise.all(
        missing.slice(offset, offset + 5).map(async ([id, number]) => {
          const history = await this.executeRpc(DepartureWSMethod.History, {
            departure_number: String(number),
            limit: 1,
            page: 1,
            need_status: true,
          });
          return (history?.data.departures as Departure[] | undefined)?.find(
            (departure) => departure.id === id,
          );
        }),
      );
      for (const departure of results) {
        if (departure) {
          restored.push(departure);
        }
      }
    }
    return restored;
  }

  markCancelReportSaved(departureId: string): void {
    this.savedCancelReportIds.add(departureId);
    this.pendingCancelReportIds.delete(departureId);
    if (this.allDeparturesController.value) {
      this.allDeparturesController.next(
        this.allDeparturesController.value.map((departure) =>
          departure.id === departureId ? { ...departure, awaitingCancelReport: false } : departure,
        ),
      );
    }
  }

  async getTeams(): Promise<boolean> {
    const response = await this.executeRpc('team:GetAll', {});
    if (!response) {
      return false;
    }

    this.teamsStateController.next(response.data);
    return this.getPendingRequests({ status: RequestStatus.Pending });
  }

  private handleTeamMessage(
    message: {
      object_number?: number;
      team_id: string;
      departure_id?: string;
      departure_number?: number;
      timestamp?: number;
    },
    teamStatus: TeamStatus,
  ): void {
    if (message.departure_id) {
      const update = {
        departureId: message.departure_id,
        number: message.departure_number ?? 0,
        stage:
          teamStatus === TeamStatus.TeamEnRoute
            ? 1
            : teamStatus === TeamStatus.TeamOnObject
              ? 2
              : 3,
        timestamp: message.timestamp ?? 0,
      };
      const current = this.teamDepartureUpdates.get(message.team_id);
      if (current) {
        if (update.departureId === current.departureId) {
          if (update.stage <= current.stage) return;
        } else if (update.number > 0 && current.number > 0) {
          if (update.number <= current.number) return;
        } else if (update.number === 0 && current.number > 0 && update.stage === 3) {
          return;
        } else if (update.timestamp !== current.timestamp) {
          if (update.timestamp < current.timestamp) return;
        } else if (update.number <= current.number) {
          return;
        }
      }
      this.teamDepartureUpdates.set(message.team_id, update);
    }
    const teamIndex = this.teamsStateController.value.findIndex((team: Team): boolean => {
      return team.id === message.team_id;
    });
    if (teamIndex === -1) return;
    const teams: Team[] = [...this.teamsStateController.value];

    teams[teamIndex] = {
      ...teams[teamIndex],
      status: teamStatus,
      object_number: message.object_number !== undefined ? message.object_number : null,
    };

    this.teamsStateController.next(teams);
  }

  private handleDepartureMessage(message: DepartureWSMessage): void {
    switch (message.detail_type) {
      case DepartureWSMessageType.Formed:
        if ('statuses' in message.detail) {
          if (this.allDeparturesController.value) {
            this.allDeparturesController.next([
              ...this.allDeparturesController.value,
              message.detail,
            ]);
          } else {
            this.temporaryDeparturesStorage.push(message.detail);
          }
          this.soundService.changeSoundState();
        }
        break;
      case DepartureWSMessageType.TeamArrived:
        this.setDepartureStatus(message, DepartureStatus.OnObject);
        break;
      case DepartureWSMessageType.DepartureAccepted:
        this.setDepartureStatus(message, DepartureStatus.EnRoute);
        break;
      case DepartureWSMessageType.DepartureCanceled:
        if ('departure_id' in message.detail) {
          const departureId = message.detail.departure_id;
          if (!this.savedCancelReportIds.has(departureId)) {
            this.pendingCancelReportIds.add(departureId);
          }
          if (this.allDeparturesController.value && !this.savedCancelReportIds.has(departureId)) {
            this.allDeparturesController.next(
              this.allDeparturesController.value.map((departure) =>
                departure.id === departureId
                  ? { ...departure, awaitingCancelReport: true }
                  : departure,
              ),
            );
          }
        }
        this.setDepartureStatus(message, DepartureStatus.Canceled);
        break;
      case DepartureWSMessageType.DepartureClosed:
        this.setDepartureStatus(message, DepartureStatus.Closed);
        break;
      case DepartureWSMessageType.DepartureCompleted:
        this.setDepartureStatus(message, DepartureStatus.Completed);
        break;
      case DepartureWSMessageType.ReceivedByTeam:
        if (this.allDeparturesController.value) {
          const departures: Departure[] = this.allDeparturesController.value.map(
            (departure: Departure): Departure => {
              if (
                'departure_id' in message.detail &&
                departure.id === message.detail.departure_id
              ) {
                return {
                  ...departure,
                  delivered_at: DateTime.now().toSeconds(),
                };
              }
              return departure;
            },
          );

          this.allDeparturesController.next(departures);
        }
        break;
    }
  }

  private setDepartureStatus(message: DepartureWSMessage, newStatus: DepartureStatus): void {
    if (this.allDeparturesController.value) {
      this.updateDeparture(message.detail, newStatus);
    } else if ('departure_id' in message.detail) {
      const departureStatus: {
        id: string;
        statuses: { status: DepartureStatus; created_at: number }[];
      } = {
        id: message.detail.departure_id,
        statuses: [{ status: newStatus, created_at: message.detail.timestamp }],
      };
      this.temporaryDeparturesStorage.push(departureStatus);
    }
  }

  private updateDeparture(
    updatedDepartureData:
      | Departure
      | {
          departure_id: string;
          timestamp: number;
          status: DepartureStatus;
        },
    newDepartureStatus: DepartureStatus,
  ): void {
    if (this.allDeparturesController.value && 'timestamp' in updatedDepartureData) {
      const departures: Departure[] = this.allDeparturesController.value.map(
        (departure: Departure): Departure => {
          if (
            'departure_id' in updatedDepartureData &&
            departure.id === updatedDepartureData.departure_id
          ) {
            const cancellationAlreadyRecorded =
              newDepartureStatus === DepartureStatus.Canceled &&
              departure.statuses.some((state) => state.status === DepartureStatus.Canceled);
            const closureAlreadyRecorded =
              newDepartureStatus === DepartureStatus.Closed &&
              departure.statuses.some((state) => state.status === DepartureStatus.Closed);
            const closedDepartureCannotReturnToCanceled =
              newDepartureStatus === DepartureStatus.Canceled &&
              departure.statuses.at(-1)?.status === DepartureStatus.Closed;

            if (
              cancellationAlreadyRecorded ||
              closureAlreadyRecorded ||
              closedDepartureCannotReturnToCanceled
            ) {
              return departure;
            }

            return {
              ...departure,
              statuses: [
                ...departure.statuses,
                { status: newDepartureStatus, created_at: updatedDepartureData.timestamp },
              ],
            };
          }
          return departure;
        },
      );

      this.allDeparturesController.next(departures);
    }
  }

  async sendWSMessage(method: DepartureWSMethod, id: string): Promise<boolean> {
    const response = await this.executeRpc(method, { departure_id: id });
    if (!response) {
      return false;
    }

    if (method === DepartureWSMethod.GetDetails) {
      this.detailsMessagesController.next(response.data);
    }
    if (method === DepartureWSMethod.GetReport) {
      this.reportMessagesController.next(response.data);
    }

    if (method === DepartureWSMethod.Delete && this.allDeparturesController.value) {
      const departures: Departure[] = this.allDeparturesController.value.map(
        (departure: Departure): Departure => {
          if (departure.id === id) {
            return { ...departure, is_deleted: true };
          }
          return departure;
        },
      );
      this.allDeparturesController.next(departures);
    }

    return true;
  }

  async sendShiftMethod(offset: number): Promise<boolean> {
    try {
      const teams = this.teamsStateController.value.length
        ? this.teamsStateController.value
        : await firstValueFrom(
            this.teams$.pipe(
              filter((currentTeams): boolean => currentTeams.length > 0),
              take(1),
              timeout(30000),
            ),
          );
      const response = await this.executeRpc('requests:GetRequests', {
        team_ids: teams.map((team: Team): string => team.id),
        limit: 16,
        offset,
      });
      if (!response) {
        return false;
      }

      this.totalRequestsNumber.set(response.data.total);
      this.requestsController.next(response.data.requests === null ? [] : response.data.requests);
      return true;
    } catch (error: unknown) {
      this.handleRpcError(error);
      return false;
    }
  }

  clearRequests(): void {
    this.requestsController.next([]);
  }

  getPendingRequests(
    filters: { team_id?: string; status?: string; greater_than?: number; less_or_equal?: number },
    limit?: number,
    offset?: number,
  ): Promise<boolean> {
    return this.loadPendingRequests(filters, limit, offset);
  }

  private async loadPendingRequests(
    filters: { team_id?: string; status?: string; greater_than?: number; less_or_equal?: number },
    limit?: number,
    offset?: number,
  ): Promise<boolean> {
    const response = await this.executeRpc('requests:GetRequests', {
      team_ids: filters.team_id
        ? [filters.team_id]
        : this.teamsStateController.value.map((team: Team) => {
            return team.id;
          }),
      statuses: filters.status ? [filters.status] : undefined,
      greater_than: filters.greater_than,
      less_or_equal: filters.less_or_equal,
      limit,
      offset,
    });
    if (!response) {
      return false;
    }

    if (filters.status && filters.status === RequestStatus.Pending) {
      this.pendingRequestsCount.set(response.data.total);
      this.pendingRequestsController.next(
        response.data.requests === null ? [] : response.data.requests,
      );
      if (limit && offset !== undefined) {
        this.totalRequestsNumber.set(response.data.total);
        this.requestsController.next(response.data.requests === null ? [] : response.data.requests);
      }
    } else {
      this.totalRequestsNumber.set(response.data.total);
      this.requestsController.next(response.data.requests === null ? [] : response.data.requests);
    }
    return true;
  }

  async declineRequest(id: string, commentary: string): Promise<boolean> {
    const response = await this.executeRpc('requests:DeclineRequest', {
      request_id: id,
      commentary,
    });
    return response !== null;
  }

  async approveRequest(id: string): Promise<boolean> {
    const response = await this.executeRpc('requests:ApproveRequest', { request_id: id });
    return response !== null;
  }

  async getRequestById(id: string): Promise<TeamRequestInfo | TeamShiftInfo | null> {
    this.currentRequestInfo.set(null);
    const response = await this.executeRpc(
      'requests:GetRequestByID',
      { request_id: id },
      DRAWER_RPC_TIMEOUT,
    );
    if (!response) {
      return null;
    }

    const requestInfo = response.data as TeamRequestInfo | TeamShiftInfo;
    this.currentRequestInfo.set(requestInfo);
    return requestInfo;
  }

  private async executeRpc(
    method: string,
    data: unknown,
    timeoutMilliseconds = RPC_TIMEOUT,
  ): Promise<RpcResult | null> {
    if (this.centrifuge.state !== State.Connected) {
      this.showConnectionError();
      return null;
    }

    try {
      return await this.withTimeout(this.centrifuge.rpc(method, data), timeoutMilliseconds);
    } catch (error: unknown) {
      this.handleRpcError(error);
      return null;
    }
  }

  private handleRpcError(error: unknown): void {
    const rpcError = error as { code?: number };
    if (rpcError.code === 109) {
      this.navbarService.setValue(90);
      this.errorNotificationService.showError(SESSION_EXPIRED_MESSAGE);
      return;
    }

    if (this.centrifuge.state !== State.Connected || rpcError.code === 1) {
      this.showConnectionError();
      return;
    }

    this.errorNotificationService.showError(RPC_ERROR_MESSAGE);
  }

  private showConnectionError(): void {
    if (this.isConnectionErrorShown) {
      return;
    }

    this.isConnectionErrorShown = true;
    this.errorNotificationService.showError(CONNECTION_ERROR_MESSAGE);
  }

  private withTimeout<T>(request: Promise<T>, timeoutMilliseconds: number): Promise<T> {
    return new Promise<T>((resolve, reject): void => {
      const timeoutId = setTimeout((): void => reject({ code: 1 }), timeoutMilliseconds);
      request.then(
        (result: T): void => {
          clearTimeout(timeoutId);
          resolve(result);
        },
        (error: unknown): void => {
          clearTimeout(timeoutId);
          reject(error);
        },
      );
    });
  }

  getDeparturesHistory(searchParams: DepartureHistorySearchParams): Promise<boolean> {
    if (!this.centrifuge || this.centrifuge.state !== State.Connected) {
      this.pendingHistorySearchParams = searchParams;
      return Promise.resolve(false);
    }

    this.pendingHistorySearchParams = null;
    return this.loadDeparturesHistory(searchParams);
  }

  private loadPendingDeparturesHistory(): void {
    if (!this.pendingHistorySearchParams) {
      return;
    }

    const searchParams = this.pendingHistorySearchParams;
    this.pendingHistorySearchParams = null;
    void this.loadDeparturesHistory(searchParams);
  }

  private async loadDeparturesHistory(
    searchParams: DepartureHistorySearchParams,
  ): Promise<boolean> {
    const response = await this.executeRpc(DepartureWSMethod.History, {
      ...searchParams,
      need_status: true,
    });
    if (!response) {
      return false;
    }
    this.historyMessagesController.next(response.data);
    return true;
  }

  async getTeamsShifts(
    greaterThan: number,
    lessOrEqual: number,
    companies: string[],
  ): Promise<boolean> {
    const response = await this.executeRpc('shifts:GetTeamsSummaries', {
      team_ids: this.teamsStateController.value.map((team: Team) => team.id),
      companies,
      greater_than: greaterThan,
      less_or_equal: lessOrEqual,
    });
    if (!response) {
      return false;
    }

    const teamsWithNames = (
      response.data as {
        company_name: string;
        summaries: {
          state: 'NoShifts' | 'NoRotations' | 'ClosedSince' | '';
          closed_since: number | null;
          team_id: string;
          team_name: string;
          summaries:
            | {
                actual_members: number;
                created_at: number;
                driver: string;
                planned_members: number;
                shift_id: string;
                team_id: string;
                shift_initiator: 'ShiftRotation' | 'ShiftOpening' | 'ShiftClosing';
                team_request_time: number;
                request_resolved_time: number;
                intern?: string;
                guard?: string;
              }[]
            | null;
        }[];
      }[]
    )
      .map((partner) => {
        return {
          ...partner,
          summaries: partner.summaries
            .map((team) => {
              return {
                ...team,
                team_name: this.teamsStateController.value.find((teamm) => {
                  return teamm.id === team.team_id;
                })!.name,
              };
            })
            .sort(sortAscendingTeamName),
        };
      })
      .sort(sortAscendingCompanyName);
    this.teamsSummaryController.next(teamsWithNames);
    if (!companies.length && !this.allPartnersControl.value.length) {
      const companyNames = teamsWithNames.map((partner) => partner.company_name);
      this.allPartnersControl.next(companyNames);
    }
    return true;
  }

  async getCompanies(): Promise<boolean> {
    const response = await this.executeRpc('companies:GetByDispatcherID', {});
    if (!response) {
      return false;
    }
    const companies = response.data.map(
      (company: { full_name: string; id: string; is_partner: boolean }) => ({
        name: company.full_name,
        id: company.id,
        isPartner: company.is_partner,
      }),
    );

    this.allStatisticsCompaniesControl.next(companies);
    return true;
  }

  async getCities(): Promise<boolean> {
    if (!this.centrifuge || this.centrifuge.state !== State.Connected) {
      this.shouldLoadCitiesAfterConnection = true;
      this.citiesLoadingErrorController.next(false);
      return false;
    }

    this.shouldLoadCitiesAfterConnection = false;
    this.citiesLoadingErrorController.next(false);
    const response = await this.executeRpc('cities:GetByDispatcherID', {});
    if (!response) {
      this.citiesLoadingErrorController.next(true);
      return false;
    }
    this.citiesController.next(response.data as MissionStatisticsCity[]);
    return true;
  }

  unsubscribeCentrifuge(): void {
    this.shouldLoadCitiesAfterConnection = false;
    this.pendingHistorySearchParams = null;

    if (!this.centrifuge) {
      return;
    }

    this.allDeparturesController.next(null);
    this.teamsStateController.next([]);
    this.teamDepartureUpdates.clear();
    this.temporaryDeparturesStorage = [];

    if (this.teamArrivedChannel) {
      this.teamArrivedChannel.unsubscribe();
      this.centrifuge.removeSubscription(this.teamArrivedChannel);
    }
    if (this.teamDepartedChannel) {
      this.teamDepartedChannel.unsubscribe();
      this.centrifuge.removeSubscription(this.teamDepartedChannel);
    }
    if (this.teamReleasedChannel) {
      this.teamReleasedChannel.unsubscribe();
      this.centrifuge.removeSubscription(this.teamReleasedChannel);
    }

    this.centrifuge.disconnect();
  }
}
